import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Modal } from 'react-bootstrap'
import { KTCard, KTCardBody } from '../../../../_metronic/helpers'
import { KTIcon } from '../../../../_metronic/helpers'
import { EditCategory } from './EditCategory'
import { 
  get_all_categories,
  delete_project_category,
  update_project_category
} from '../../projects/api/projectCategoriesApi'
import { get_all_subcategories_of_category } from '../../digital-assets/api/downloadAssetsApi'
import { toast } from 'react-toastify'
import { LoadingProgressBar } from '../../projects/components/LoadingProgressBar'
import { permissionCheck } from '../../../helper-functions/PermissionCheck'

// Category type for this module
interface CategoryItem {
  id: number
  name: string
  description: string
  created_at: string
  updated_at: string
  status: number
}

const CategoriesList: React.FC = () => {
  const navigate = useNavigate()
  const [categories, setCategories] = useState<CategoryItem[]>([])
  const [filteredCategories, setFilteredCategories] = useState<CategoryItem[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStatus, setSelectedStatus] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<CategoryItem | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [categoryToDelete, setCategoryToDelete] = useState<CategoryItem | null>(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedCategoryForDetails, setSelectedCategoryForDetails] = useState<CategoryItem | null>(null)
  const [subcategories, setSubcategories] = useState<any[]>([])
  const [isLoadingSubcategories, setIsLoadingSubcategories] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const canViewCategories = Boolean(permissionCheck('category-view'))
  const canCreateCategory = Boolean(permissionCheck('category-create'))
  const canUpdateCategory = Boolean(permissionCheck('category-update'))
  const canDeleteCategory = Boolean(permissionCheck('category-delete'))
  const canViewCategoryLogs = Boolean(permissionCheck('category-activity-log'))

  // Load categories
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true)
        if (canViewCategories) {
          await fetchAllCategories()
        }
      } catch (error) {
        console.error('Error loading categories:', error)
        toast.error('Failed to load categories')
      } finally {
        setIsLoading(false)
      }
    }
    if (canViewCategories) {
      loadData()
    }
  }, [canViewCategories])

  const fetchAllCategories = async () => {
    try {
      const res = await get_all_categories()
      const apiCats = res?.data?.all_categories || res?.data?.categories || res?.data || []
      const all: CategoryItem[] = (Array.isArray(apiCats) ? apiCats : []).map((c: any) => ({
        id: Number(c.id),
        name: c.category_name,
        description: c.description || '',
        created_at: c.created_at,
        updated_at: c.updated_at,
        status: Number(c.status)
      }))
      setCategories(all)
      setFilteredCategories(all)
    } catch (error) {
      console.error('Error fetching categories:', error)
      toast.error('Failed to load categories')
      setCategories([])
      setFilteredCategories([])
    }
  }

  // Filter categories based on search term and status
  useEffect(() => {
    let filtered = categories

    if (searchTerm) {
      filtered = filtered.filter(category =>
        category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.description.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (selectedStatus !== '') {
      filtered = filtered.filter(category => String(category.status) === selectedStatus)
    }

    setFilteredCategories(filtered)
  }, [categories, searchTerm, selectedStatus])

  // Pagination derived values
  const totalPages = Math.max(1, Math.ceil(filteredCategories.length / pageSize))
  const pageSafe = Math.min(currentPage, totalPages)
  const startIdx = (pageSafe - 1) * pageSize
  const endIdx = startIdx + pageSize
  const paginatedCategories = filteredCategories.slice(startIdx, endIdx)

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, selectedStatus, pageSize])


  const handleEditCategory = async (updatedCategory: CategoryItem) => {
    if (!canUpdateCategory) {
      toast.error('You do not have permission to update categories')
      return
    }
    try {
      const payload = {
        category_name: updatedCategory.name,
        description: updatedCategory.description,
        status: String(updatedCategory.status)
      }
      await update_project_category(String(updatedCategory.id), payload)
      toast.success('Category updated')
      // Refresh all categories and rely on filters
      await fetchAllCategories()
      setShowEditModal(false)
      setSelectedCategory(null)
    } catch (error: any) {
      console.error('Error updating category:', error)
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update category'
      toast.error(errorMessage)
    }
  }

  const handleDeleteClick = (category: CategoryItem) => {
    if (!canDeleteCategory) {
      toast.error('You do not have permission to delete categories')
      return
    }
    setCategoryToDelete(category)
    setShowDeleteModal(true)
  }

  const handleDeleteConfirm = async () => {
    if (!canDeleteCategory) {
      toast.error('You do not have permission to delete categories')
      return
    }
    if (!categoryToDelete) return
    try {
      await delete_project_category(String(categoryToDelete.id))
      toast.success('Category deleted')
      await fetchAllCategories()
    } catch (error: any) {
      console.error('Error deleting category:', error)
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete category'
      toast.error(errorMessage)
    } finally {
      setShowDeleteModal(false)
      setCategoryToDelete(null)
    }
  }

  const handleEditClick = (category: CategoryItem) => {
    if (!canUpdateCategory) {
      toast.error('You do not have permission to update categories')
      return
    }
    setSelectedCategory(category)
    setShowEditModal(true)
  }

  const truncateText = (text: string, maxLength: number = 100) => {
    if (!text) return ''
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  const handleViewDetails = async (category: CategoryItem) => {
    setSelectedCategoryForDetails(category)
    setShowDetailsModal(true)
    setIsLoadingSubcategories(true)
    setSubcategories([])
    
    try {
      const response = await get_all_subcategories_of_category(String(category.id))
      // API returns data in "all_categories_of_category" key
      const apiSubcats = response?.data?.all_categories_of_category || response?.data?.all_subcategories || response?.data?.subcategories || response?.data || []
      const allSubcats = (Array.isArray(apiSubcats) ? apiSubcats : []).map((sc: any) => ({
        id: Number(sc.id),
        subcategory_name: sc.subcategory_name || sc.name || '',
        description: sc.description || '',
        status: Number(sc.status) || 0,
        created_at: sc.created_at || '',
        updated_at: sc.updated_at || ''
      }))
      setSubcategories(allSubcats)
    } catch (error) {
      console.error('Error loading subcategories:', error)
      toast.error('Failed to load subcategories')
      setSubcategories([])
    } finally {
      setIsLoadingSubcategories(false)
    }
  }

  if (!canViewCategories) {
    return (
      <KTCard>
        <KTCardBody>
          <div className='alert alert-warning mb-0'>
            You do not have permission to view categories.
          </div>
        </KTCardBody>
      </KTCard>
    )
  }

  return (
    <>
      <LoadingProgressBar isLoading={isLoading} />
      <KTCard>
        <KTCardBody>
          <div className='d-flex flex-column flex-lg-row'>
            <div className='flex-lg-row-fluid mb-10 mb-lg-0 me-lg-7'>
              <h3 className='fw-bold text-dark'>Categories Management</h3>
              <p className='text-muted fs-6'>Manage your categories</p>
            </div>
            {canCreateCategory && (
              <div className='d-flex align-items-center'>
                <button
                  className='btn btn-primary'
                  onClick={() => navigate('/categories/add')}
                >
                  <KTIcon iconName='plus' className='fs-2' />
                  Add Category
                </button>
              </div>
            )}
          </div>
        </KTCardBody>
      </KTCard>

      <KTCard>
        <KTCardBody>
          {/* Search and Filter Section */}
          <div className='d-flex align-items-center gap-3 flex-wrap mb-6'>
            <div className='position-relative' style={{minWidth: 280}}>
              <KTIcon
                iconName='magnifier'
                className='fs-3 position-absolute top-50 translate-middle-y ms-3'
              />
              <input
                type='text'
                className='form-control form-control-solid ps-13'
                placeholder='Search categories...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{minWidth: 280}}
              />
            </div>
            <div style={{minWidth: 160}}>
              <select
                className='form-select form-select-solid'
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value=''>All Status</option>
                <option value='1'>Active</option>
                <option value='0'>Inactive</option>
              </select>
            </div>
            <div className='ms-auto'>
              <button
                className='btn btn-light-primary'
                onClick={() => {
                  setSearchTerm('')
                  setSelectedStatus('')
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* Categories Table */}
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-bold text-muted'>
                  <th className='min-w-150px'>Name</th>
                  <th className='min-w-200px'>Description</th>
                  <th className='min-w-80px'>Status</th>
                  <th className='min-w-100px'>Updated At</th>
                  <th className='min-w-100px text-end'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({length: 6}).map((_, idx) => (
                    <tr key={`sk-${idx}`}>
                      <td>
                        <div className='placeholder-glow'>
                          <span className='placeholder col-8'></span>
                        </div>
                      </td>
                      <td>
                        <div className='placeholder-glow'>
                          <span className='placeholder col-10'></span>
                        </div>
                      </td>
                      <td>
                        <div className='placeholder-glow'>
                          <span className='placeholder col-6'></span>
                        </div>
                      </td>
                      <td>
                        <div className='placeholder-glow'>
                          <span className='placeholder col-4'></span>
                        </div>
                      </td>
                      <td>
                        <div className='placeholder-glow'>
                          <span className='placeholder col-5'></span>
                        </div>
                      </td>
                      <td className='text-end'>
                        <div className='d-flex justify-content-end flex-shrink-0 placeholder-glow'>
                          <span className='placeholder btn btn-icon btn-bg-light btn-sm me-1 col-2'></span>
                          <span className='placeholder btn btn-icon btn-bg-light btn-sm col-2'></span>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : filteredCategories.length === 0 ? (
                  <tr>
                    <td colSpan={5} className='text-center py-10'>
                      <div className='text-muted'>No categories found</div>
                    </td>
                  </tr>
                ) : (
                  paginatedCategories.map((category) => (
                    <tr key={category.id}>
                      <td>
                        <div className='d-flex align-items-center'>
                          <div className='d-flex justify-content-start flex-column'>
                            <span className='text-dark fw-bold text-hover-primary fs-6'>
                              {category.name}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span 
                          className='text-muted fw-bold text-muted d-block fs-7'
                          style={{
                            maxWidth: '250px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={category.description || 'N/A'}
                        >
                          {category.description || 'N/A'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${category.status === 1 ? 'badge-light-success' : 'badge-light-danger'} fw-bold`}>
                          {category.status === 1 ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <span className='text-muted fw-bold text-muted d-block fs-7'>
                          {category.updated_at ? new Date(category.updated_at).toLocaleDateString() : 'N/A'}
                        </span>
                      </td>
                      <td className='text-end'>
                        <div className='d-flex justify-content-end flex-shrink-0'>
                          <button
                            className='btn btn-icon btn-bg-light btn-active-color-info btn-sm me-1'
                            onClick={() => handleViewDetails(category)}
                            title='View Details'
                          >
                            <KTIcon iconName='eye' className='fs-3' />
                          </button>
                          {canViewCategoryLogs && (
                            <button
                              className='btn btn-icon btn-bg-light btn-active-color-warning btn-sm me-1'
                              onClick={() => navigate("/projects/single-projectcategory-log", { state: { categoryId: category.id } })}
                              title='Activity Logs'
                            >
                              <i className='bi bi-journal-text fs-5'></i>
                            </button>
                          )}
                          {canUpdateCategory && (
                            <button
                              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                              onClick={() => handleEditClick(category)}
                            >
                              <KTIcon iconName='pencil' className='fs-3' />
                            </button>
                          )}
                          {canDeleteCategory && (
                            <button
                              className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm'
                              onClick={() => handleDeleteClick(category)}
                            >
                              <KTIcon iconName='trash' className='fs-3' />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination controls */}
          <div className='d-flex align-items-center justify-content-between mt-4'>
            <div className='d-flex align-items-center gap-3'>
              <span className='text-muted fs-7'>
                Showing {filteredCategories.length === 0 ? 0 : startIdx + 1}-{Math.min(endIdx, filteredCategories.length)} of {filteredCategories.length}
              </span>
              <div className='d-flex align-items-center'>
                <span className='me-2 text-muted fs-7'>Show</span>
                <select
                  className='form-select form-select-sm w-auto'
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span className='ms-2 text-muted fs-7'>per page</span>
              </div>
            </div>
            <ul className='pagination'>
              <li className={`page-item ${pageSafe === 1 ? 'disabled' : ''}`}>
                <button className='page-link' onClick={() => setCurrentPage(1)}>«</button>
              </li>
              <li className={`page-item ${pageSafe === 1 ? 'disabled' : ''}`}>
                <button className='page-link' onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>Prev</button>
              </li>
              <li className='page-item active'>
                <span className='page-link'>{pageSafe} / {totalPages}</span>
              </li>
              <li className={`page-item ${pageSafe === totalPages ? 'disabled' : ''}`}>
                <button className='page-link' onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>Next</button>
              </li>
              <li className={`page-item ${pageSafe === totalPages ? 'disabled' : ''}`}>
                <button className='page-link' onClick={() => setCurrentPage(totalPages)}>»</button>
              </li>
            </ul>
          </div>
        </KTCardBody>
      </KTCard>


      {/* Edit Category Modal */}
      {showEditModal && selectedCategory && (
        <EditCategory
          category={selectedCategory}
          onClose={() => {
            setShowEditModal(false)
            setSelectedCategory(null)
          }}
          onSave={handleEditCategory}
        />
      )}

      {/* Category Details Modal */}
      <Modal
        className='modal fade'
        id='kt_modal_category_details'
        tabIndex={-1}
        role='dialog'
        show={showDetailsModal}
        onHide={() => {
          setShowDetailsModal(false)
          setSelectedCategoryForDetails(null)
          setSubcategories([])
        }}
        size="lg"
      >
        <div className='modal-dialog modal-dialog-centered'>
          <div className='modal-content'>
            <div className='modal-header pb-0 border-0'>
              <h2 className='modal-title'>Category Details</h2>
              <div
                className='btn btn-sm btn-icon btn-active-color-primary'
                onClick={() => {
                  setShowDetailsModal(false)
                  setSelectedCategoryForDetails(null)
                  setSubcategories([])
                }}
              >
                <KTIcon iconName='cross' className='fs-1' />
              </div>
            </div>
            <div className='modal-body scroll-y mx-5 mx-xl-18 pt-0 pb-15'>
              {selectedCategoryForDetails && (
                <>
                  {/* Category Information */}
                  <div className='mb-10'>
                    <h3 className='mb-3'>{selectedCategoryForDetails.name}</h3>
                    {selectedCategoryForDetails.description && (
                      <p className='text-muted mb-4'>{selectedCategoryForDetails.description}</p>
                    )}
                    <div className='d-flex flex-wrap gap-4 mb-4'>
                      <div>
                        <span className='text-muted fs-7 d-block'>Status</span>
                        <span className={`badge ${selectedCategoryForDetails.status === 1 ? 'badge-light-success' : 'badge-light-danger'} fw-bold`}>
                          {selectedCategoryForDetails.status === 1 ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      {selectedCategoryForDetails.updated_at && (
                        <div>
                          <span className='text-muted fs-7 d-block'>Updated</span>
                          <span className='text-dark fw-bold'>
                            {new Date(selectedCategoryForDetails.updated_at).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Subcategories Section */}
                  <div className='separator separator-dashed my-6'></div>
                  <div>
                    <h4 className='mb-4'>
                      <KTIcon iconName='category' className='me-2' />
                      Subcategories ({subcategories.length})
                    </h4>
                    
                    {isLoadingSubcategories ? (
                      <div className='text-center py-5'>
                        <div className='spinner-border text-primary' role='status'>
                          <span className='visually-hidden'>Loading...</span>
                        </div>
                        <div className='text-muted mt-3'>Loading subcategories...</div>
                      </div>
                    ) : subcategories.length === 0 ? (
                      <div className='text-center py-5'>
                        <KTIcon iconName='file' className='fs-3x text-muted mb-3' />
                        <div className='text-muted'>No subcategories found for this category</div>
                      </div>
                    ) : (
                      <div className='table-responsive'>
                        <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                          <thead>
                            <tr className='fw-bold text-muted'>
                              <th className='min-w-150px'>Subcategory Name</th>
                              <th className='min-w-200px'>Description</th>
                              <th className='min-w-80px'>Status</th>
                              <th className='min-w-100px'>Updated At</th>
                            </tr>
                          </thead>
                          <tbody>
                            {subcategories.map((subcat) => (
                              <tr key={subcat.id}>
                                <td>
                                  <span className='text-dark fw-bold'>
                                    {subcat.subcategory_name}
                                  </span>
                                </td>
                                <td>
                                  <span 
                                    className='text-muted fs-7'
                                    style={{
                                      maxWidth: '250px',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                    title={subcat.description || 'N/A'}
                                  >
                                    {subcat.description || 'N/A'}
                                  </span>
                                </td>
                                <td>
                                  <span className={`badge ${subcat.status === 1 ? 'badge-light-success' : 'badge-light-danger'} fw-bold`}>
                                    {subcat.status === 1 ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                                <td>
                                  <span className='text-muted fs-7'>
                                    {subcat.updated_at ? new Date(subcat.updated_at).toLocaleDateString() : 'N/A'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className='modal-footer'>
              <button
                className='btn btn-light'
                onClick={() => {
                  setShowDetailsModal(false)
                  setSelectedCategoryForDetails(null)
                  setSubcategories([])
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        className='modal fade'
        id='kt_modal_delete_category'
        tabIndex={-1}
        role='dialog'
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false)
          setCategoryToDelete(null)
        }}
      >
        <div className='modal-dialog modal-dialog-centered mw-650px'>
          <div className='modal-content'>
            <div className='modal-header pb-0 border-0 justify-content-end'>
              <div
                className='btn btn-sm btn-icon btn-active-color-primary'
                onClick={() => {
                  setShowDeleteModal(false)
                  setCategoryToDelete(null)
                }}
              >
                <KTIcon iconName='cross' className='fs-1' />
              </div>
            </div>
            <div className='modal-body scroll-y mx-5 mx-xl-18 pt-0 pb-15'>
              <div className='mb-13 text-center'>
                <KTIcon iconName='question' className='fs-5tx text-danger mb-5' />
                <h1 className='mb-3'>Delete Category</h1>
                <div className='text-muted fw-semibold fs-5'>
                  Are you sure you want to delete this category?
                  {categoryToDelete && (
                    <div className='mt-3'>
                      <strong className='text-dark'>"{categoryToDelete.name}"</strong>
                    </div>
                  )}
                  <br />This action cannot be undone.
                </div>
              </div>
              <div className='d-flex flex-stack'>
                <button
                  className='btn btn-primary'
                  onClick={() => {
                    setShowDeleteModal(false)
                    setCategoryToDelete(null)
                  }}
                >
                  Cancel
                </button>
                <button className='btn btn-danger' onClick={handleDeleteConfirm}>
                  Delete Category
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}

export { CategoriesList }
